import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';
import { formatCurrency } from "../utils/currencyHelper";
// --- Redux Actions ---
import { listAllDoctorPayoutsForAdmin } from 'src/actions/doctorPayoutAction';
import { getAllDoctors } from 'src/actions/doctorActions';
import { listDoctorInvoices } from 'src/actions/invoiceActions';
import { getAllPatientProfiles } from 'src/actions/patientProfileActions';
import { imgUrl } from 'src/actions/types';

// --- Helper Functions ---
const getPayoutBadgeClass = (status) => {
    switch (status) {
        case 'completed': case 'processed': return 'complete-label';
        case 'processing': case 'queued': return 'processing-label';
        case 'failed': case 'reversed': return 'failed-label';
        default: return 'edited-label';
    }
};

const getDisplayStatus = (status) => {
    switch (status) {
        case 'completed':
        case 'processed':
            return 'Success';
        case 'processing':
        case 'queued':
            return 'Processing';
        case 'failed':
        case 'reversed':
            return 'Failed';
        default:
            return status; // Handles 'Awaiting' or other statuses
    }
};

const formatDate = (dateString, options = { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric' }) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString(undefined, options);
};

// --- Main Component ---
const AdminPayoutDashboard = () => {
    const dispatch = useDispatch();

    // --- Redux State ---
    const { loading, error, payoutRecords } = useSelector((state) => state.doctorPayout);
    const { selectedCurrency } = useSelector((state) => state.currencyReducer);
    const { doctors } = useSelector((state) => state.doctorList);
    const { invoices } = useSelector((state) => state.invoice);
    const { profiles: patients } = useSelector((state) => state.patientProfile);

    // --- Local State ---
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    // --- Data Fetching ---
    useEffect(() => {
        dispatch(listAllDoctorPayoutsForAdmin());
        dispatch(getAllDoctors());
        dispatch(listDoctorInvoices());
        dispatch(getAllPatientProfiles());
    }, [dispatch]);

    // --- Data Processing Logic ---
    const enrichedPayoutRecords = useMemo(() => {
        // ** FIX **: Base the initial check ONLY on the primary data source.
        // Don't return an empty array if supplementary data is still loading.
        if (!payoutRecords) return [];

        // These maps will be created empty if the data isn't ready, which is handled gracefully below.
        const doctorMap = doctors.length > 0 ? new Map(doctors.map(d => [d.hospitalID, d])) : new Map();
        const invoiceMap = invoices.length > 0 ? new Map(invoices.map(i => [String(i.id), i])) : new Map();
        const patientMap = patients.length > 0 ? new Map(patients.map(p => [p.PatientID, p])) : new Map();
        
        const enriched = payoutRecords.map(record => {
            // The `|| {}` ensures that if a doctor/invoice is not found, the app doesn't crash.
            const invoiceDetails = invoiceMap.get(String(record.invoiceId || record.InvoiceId)) || {};
            const patientDetails = patientMap.get(invoiceDetails.PatientID) || {};
            const doctorDetails = doctorMap.get(record.hospitalID) || {};
            return { ...record, doctorDetails, invoiceDetails, patientDetails };
        });

        const sorted = enriched.sort((a, b) => {
            const dateA = a.date ? new Date(a.date) : new Date(a.createdAt);
            const dateB = b.date ? new Date(b.date) : new Date(b.createdAt);
            return dateB - dateA;
        });

        if (!searchTerm) {
            return sorted;
        }
        
        return sorted.filter(record => {
            const doctorName = `${record.doctorDetails.firstName || ''} ${record.doctorDetails.lastName || ''}`.toLowerCase();
            const payoutId = record.payoutId || record.payoutid || '';
            return payoutId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                   (doctorName.trim() && doctorName.includes(searchTerm.toLowerCase()));
        });

    }, [payoutRecords, doctors, invoices, patients, searchTerm]);

const indexOfLastItem = currentPage * itemsPerPage;
const indexOfFirstItem = indexOfLastItem - itemsPerPage;
const currentItems = enrichedPayoutRecords.slice(indexOfFirstItem, indexOfLastItem);
const totalPages = Math.ceil(enrichedPayoutRecords.length / itemsPerPage);

useEffect(() => {
    setCurrentPage(1); // Filter ya items badalne par page 1 par jayein
}, [searchTerm, itemsPerPage]);

    // --- Modal Handlers ---
    const handleViewDetailsClick = (record) => {
        setSelectedRecord(record);
        setShowDetailsModal(true);
    };

    const handleCloseModal = () => {
        setShowDetailsModal(false);
        setSelectedRecord(null);
    };
const renderItemsPerPageSelector = () => (
    <div className="dataTables_length">
        <label>Items: 
            <select className="form-control form-control-sm ml-2" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
            </select>
        </label>
    </div>
);

const renderPagination = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

    return (
        <div className="d-flex align-items-center">
            <div className="dataTables_info">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, enrichedPayoutRecords.length)} of {enrichedPayoutRecords.length} entries
            </div>
            <ul className="pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>Previous</button>
                </li>
                {pageNumbers.map(number => (
                    <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(number)}>{number}</button>
                    </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
                </li>
            </ul>
        </div>
    );
};
    return (
        
        <div className="page-wrapper ml-0">
            
<style>{`
    .pagination-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        margin-top: 20px;
        padding: 15px 0;
        border-top: 1px solid #dee2e6;
    }
    .dataTables_length label { display: inline-flex; align-items: center; font-weight: normal; margin-bottom: 0; }
    .dataTables_length select { margin-left: 10px; width: 70px; border-radius: 4px; border: 1px solid #ddd; padding: 5px; }
    .pagination .page-item.active .page-link { background-color: #0084d9; border-color: #0084d9; color: white; }
    .pagination .page-link { color: #0084d9; padding: 8px 14px; border: 1px solid #dee2e6; margin: 0 2px; border-radius: 4px; background: none; }
    .dataTables_info { font-size: 0.9rem; color: #6c757d; margin-right: 20px; }
`}</style>
            <div className="content-container">

                {/* Header Section */}
                 <div className="page-header filter-bar rounded">
                          <div className="row align-items-center">
                            <div className="col">
                              <h4 className="page-title">All Doctor Payouts</h4>
                              <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                  <Link to="/doctor/dashboard">Admin Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item active">Payouts</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                

                {/* Table Section */}
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-body">
                                <div className="pb-3">
                                    <InputGroup>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search by Payout ID or Doctor Name..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </InputGroup>
                                </div>
                                <div className="table-responsive">
                                    <table className="datatable table table-hover table-center mb-0">
                                        <thead>
                                            <tr>
                                                <th>Payout ID</th>
                                                <th>Doctor</th>
                                                <th>Invoice ID</th>
                                                <th>Amount</th>
                                                <th className="text-center">Status</th>
                                                <th>Payout Date</th>
                                                <th className="text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {/* ** FIX **: Use the primary `loading` flag from the main data source. */}
                                            {loading ? (
                                                <tr><td colSpan="7" className="text-center">Loading Payout Data...</td></tr>
                                            ) : error ? (
                                                <tr><td colSpan="7" className="text-center text-danger">Error: {error}</td></tr>
                                            ) : enrichedPayoutRecords.length > 0 ? (
                                                currentItems.map((record) => (
                                                    <tr key={record.id}>
                                                        <td style={{ wordBreak: 'break-all' }}>{record.payoutId || record.payoutid || 'N/A'}</td>
                                                        <td>
                                                            <h2 className="table-avatar">
                                                                <Link to={`/admin/doctor-profile/${record.doctorDetails.id || '#'}`} className="avatar avatar-sm mr-2">
                                                                    <img className="avatar-img rounded-circle" src={record.doctorDetails.uploadPhoto ? `${imgUrl}${record.doctorDetails.uploadPhoto}` : '/assets/img/doctors/doctor-thumb-01.jpg'} alt="Doctor" />
                                                                </Link>
                                                                <Link to={`/admin/doctor-profile/${record.doctorDetails.id || '#'}`}>{record.doctorDetails.firstName || 'Unknown'} {record.doctorDetails.lastName || ''}</Link>
                                                            </h2>
                                                        </td>
                                                        <td>#{record.invoiceId || record.InvoiceId}</td>
                                                        <td>{formatCurrency(record.Amount, selectedCurrency)}</td>
                                                        <td className="text-center">
                                                            <span className={`badge badge-pill ${getPayoutBadgeClass(record.paymentStatus)}`}>
                                                                {getDisplayStatus(record.paymentStatus)}
                                                            </span>
                                                        </td>
                                                        <td>{formatDate(record.date || record.createdAt)}</td>
                                                        <td className="text-right">
                                                            <button className="i-btn-view" onClick={() => handleViewDetailsClick(record)}>
                                                            
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr><td colSpan="7" className="text-center">No payouts found.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                {enrichedPayoutRecords.length > 0 && (
    <div className="pagination-wrapper">
        <div className="items-per-page-wrapper">
            {renderItemsPerPageSelector()}
        </div>
        <div className="pagination-controls-wrapper">
            {renderPagination()}
        </div>
    </div>
)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {selectedRecord && (
                <Modal show={showDetailsModal} onHide={handleCloseModal} centered size="lg">
                    <Modal.Header className='bg-info text-white ' closeButton closeVariant="white">
                        <Modal.Title>Payout Record Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p><strong>Database Record ID:</strong> {selectedRecord.id}</p>
                        <p><strong>Payout ID (Gateway):</strong> <span className="text-primary">{selectedRecord.payoutId || selectedRecord.payoutid || 'N/A'}</span></p>
                        <hr/>
                        <p><strong>Doctor:</strong> Dr. {selectedRecord.doctorDetails.firstName} {selectedRecord.doctorDetails.lastName} (Hospital ID: {selectedRecord.hospitalID})</p>
                        <p><strong>Patient:</strong> {selectedRecord.patientDetails.firstName || 'Unknown'} {selectedRecord.patientDetails.lastName || ''} (Patient ID: {selectedRecord.invoiceDetails.PatientID})</p>
                        <hr/>
                        <p><strong>Invoice ID:</strong> #{selectedRecord.invoiceId || selectedRecord.InvoiceId}</p>
                       <p><strong>Amount:</strong> {formatCurrency(selectedRecord.Amount, selectedCurrency)}</p>
                        <p><strong>Status:</strong> {getDisplayStatus(selectedRecord.paymentStatus)}</p>
                        <p><strong>Payout Date:</strong> {formatDate(selectedRecord.date)}</p>
                        <p><strong>Record Created On:</strong> {formatDate(selectedRecord.createdAt)}</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className = "i-btn-cancel"  onClick={handleCloseModal}>
                         
                        </button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
}

export default AdminPayoutDashboard;